import React, { memo, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { defineMessages } from 'react-intl';

import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { FlexColumn } from 'ui-components/components/flex';
// This is for a Pendo button that has not been implemented yet
// import { Button } from 'ui-components/components/button';
import { GlobalEventListenerRoot } from 'ui-components/components/global-event-listener/root';
import { Text, TextType } from 'ui-components/components/text';

import { AmazonRedactedInfoBanner } from './amazon-redacted-info-banner/amazon-redacted-info-banner';
import { CreateReturnContextProvider } from './context/create-return-provider';
import { CustomizeLayoutProvider } from './custom-layout/customize-layout-provider';
import OrderDetailsNavigationContextProvider from './context/navigation/order-details-navigation-context-provider';
import { usePendingReturnContext } from './context/pending-return-context-provider';
import { Drawer } from './drawer';
import { OrderDetailsExpandedSectionContextProvider } from './fulfillment-plan/order-details-expanded-section-provider';
import greenStar from './green-star.svg';
import { OverlayNavigationButtons } from './navigation/overlay-navigation-buttons';
import style from './order-details-canvas.module.less';
import { OrderDetailsNavBar } from './order-details-nav-bar/order-details-nav-bar';
import { OrderDetailsSelectedContentContainer } from './selected-content/order-details-selected-content-container';
import { SidePanelContextProvider } from './side-panel';
import { Sidebar } from './sidebar/sidebar';
import CloseModalHotkey from '../../components/hotkeys/close-modal';
import { useScreenSizeContext } from '../../contexts/screen-size-resolution';
import { useFulfillmentPlanIdContext } from '../../fulfillment-plans/context/fulfillment-plan-id-context-provider';
import { useSalesOrderIdContext } from '../context/sales-order-id-context-provider';
import { ReturnsPanelContextProvider } from './side-panel/returns/returns-panel-context-provider';
import { ShipmentsPanelContextProvider } from './side-panel/shipments/shipments-panel-context-provider';
import ErrorBoundary from '../../components/error-boundary';
import { useContainsDemoOrder } from '../../demos/hooks/use-contains-demo-order';
import { ResetDemoOrderButton } from '../../demos/reset-demo-order-button';
import { closedOrderDetailModal } from '../../store/orders/order-details/orders.details.actions';

const messages = defineMessages({
  takeATourBanner: {
    description: 'Text for demo order details banner',
    defaultMessage: 'Welcome! Hover over any star icon to learn more about that feature.',
    id: 'hsp/5v',
  },

  showList: {
    description: 'Text for tour list button',
    defaultMessage: 'Show tour list',
    id: 't5s6ve',
  },
});

const OrderDetailsCanvasComponent = () => {
  const dispatch = useDispatch();
  const { hasPendingReturn, cancelPendingReturnToCloseOrderDetails } = usePendingReturnContext();
  const salesOrderId = useSalesOrderIdContext();
  const closeOrderDetails = useCallback(() => {
    if (hasPendingReturn) {
      cancelPendingReturnToCloseOrderDetails();
    } else {
      dispatch(closedOrderDetailModal());
    }
  }, [cancelPendingReturnToCloseOrderDetails, dispatch, hasPendingReturn]);
  const { isMobile, isTablet } = useScreenSizeContext();
  const showSmallScreenNavigation = isMobile || isTablet;
  const fulfillmentPlanId = useFulfillmentPlanIdContext();
  const isDemo = useContainsDemoOrder([fulfillmentPlanId]);

  return (
    <Drawer onClose={closeOrderDetails}>
      <FlexColumn className={style['order-details-drawer']}>
        <GlobalEventListenerRoot>
          <CustomizeLayoutProvider>
            <OrderDetailsNavBar onClose={closeOrderDetails} showNavigationButtons={showSmallScreenNavigation} />
            <ErrorBoundary errorMessageId='orderDetails'>
              <FlexColumn className={style['order-details-drawer-body']}>
                <AmazonRedactedInfoBanner salesOrderId={salesOrderId} />
                <div className={style['order-details-sidebar-and-selected-content-container']}>
                  <OrderDetailsExpandedSectionContextProvider>
                    <SidePanelContextProvider>
                      <ShipmentsPanelContextProvider>
                        <ReturnsPanelContextProvider>
                          <CreateReturnContextProvider salesOrderId={salesOrderId}>
                            <Sidebar />
                            <FlexColumn className={style['order-details-content-wrapper']}>
                              {isDemo ? (
                                <BannerMessageV2
                                  className={style['demo-banner']}
                                  content={
                                    <>
                                      <img alt='Green star icon' src={greenStar} />
                                      <Text textType={TextType.Paragraph} value={messages.takeATourBanner} />
                                      {/* This is for the Pendo portion which is on hold for the moment */}
                                      {/* <Button className={style['show-list-button']}>
                                          <Text
                                            textType={TextType.Label}
                                            value={messages.showList}
                                          />
                                        </Button> */}
                                      <ResetDemoOrderButton />
                                    </>
                                  }
                                  status={BannerStatus.Custom}
                                />
                              ) : null}
                              <OrderDetailsSelectedContentContainer />
                            </FlexColumn>
                          </CreateReturnContextProvider>
                        </ReturnsPanelContextProvider>
                      </ShipmentsPanelContextProvider>
                    </SidePanelContextProvider>
                  </OrderDetailsExpandedSectionContextProvider>
                </div>
              </FlexColumn>
            </ErrorBoundary>
            {!showSmallScreenNavigation && (
              <OrderDetailsNavigationContextProvider>
                <OverlayNavigationButtons />
              </OrderDetailsNavigationContextProvider>
            )}
            <CloseModalHotkey onClose={closeOrderDetails} />
          </CustomizeLayoutProvider>
        </GlobalEventListenerRoot>
      </FlexColumn>
    </Drawer>
  );
};

export const OrderDetailsCanvas = memo(OrderDetailsCanvasComponent);
